/* istanbul ignore file */
import React from 'react'
import PropTypes from 'prop-types'
import { connect } from 'react-redux'
import smoothscroll from 'smoothscroll-polyfill'
import { MEDIUM_WIDTH } from '../../constants'
import * as TutorialTooltipActions from './actions'
import TutorialTooltipReducer from './reducers'
import TutorialTooltip, {
  TutorialTooltipGroup,
  TutorialTooltipGroupItem,
} from './components'
import { TutorialTooltipGroupContext } from './util/contexts'

const TOP_CENTER = 'top center'

const scrollToRef = (refOffset, offset = 0) =>
  window.scrollTo({
    top: refOffset - offset,
    behavior: 'smooth',
  })

// Offset relative to the top of the browser/screen
const getBoundingClientRectTopOffset = ({ current }) => {
  if (current) {
    return current.getBoundingClientRect().top
  }
}

// Offset relative to the top of the document
const getBoundingClientRectTopPosition = ({ current }) => {
  if (current) {
    return current.getBoundingClientRect().top + window.pageYOffset
  }
}

class TutorialTooltipContainer extends React.Component {
  static propTypes = {
    steps: PropTypes.array.isRequired,
    tutorial: PropTypes.number.isRequired,
    currentTutorial: PropTypes.number.isRequired,
    incrementTutorial: PropTypes.func.isRequired,
    setTutorial: PropTypes.func.isRequired,
    registerStep: PropTypes.func.isRequired,
    unregisterStep: PropTypes.func.isRequired,
    children: PropTypes.node.isRequired,
    nextButtonContent: PropTypes.node,
    offset: PropTypes.number,
    mobileOffset: PropTypes.number,
    position: PropTypes.string,
    mobilePosition: PropTypes.string,
    elmId: PropTypes.string,
    skip: PropTypes.bool,
    header: PropTypes.node,
    content: PropTypes.node,
    next: PropTypes.number,
    group: PropTypes.bool,
    fade: PropTypes.bool,
    closeOnDimmerClick: PropTypes.bool,
    handleNext: PropTypes.func,
  }

  static defaultProps = {
    elmId: 'tutorial',
    group: false,
    fade: true,
    skip: false,
    closeOnDimmerClick: false,
    offset: 100,
    mobileOffset: 240,
    position: TOP_CENTER,
    mobilePosition: TOP_CENTER,
  }

  _isMounted = false

  childRef = React.createRef()

  componentDidMount() {
    if (!this.props.skip) this.props.registerStep(this.props.tutorial)
    this._isMounted = true
    smoothscroll.polyfill()
  }

  componentWillUnmount() {
    this.props.unregisterStep(this.props.tutorial)
    this._isMounted = false
  }

  componentDidUpdate(prevProps) {
    const { tutorial, currentTutorial, skip, registerStep, unregisterStep } =
      this.props
    if (prevProps.skip !== skip)
      skip ? unregisterStep(tutorial) : registerStep(tutorial)
    const newCurrentTutorial = prevProps.currentTutorial !== currentTutorial
    const tutorialSelected = currentTutorial === tutorial
    const tutorialWasSelected = prevProps.currentTutorial === tutorial
    const rootCurrentTutorial = currentTutorial === 0
    if (newCurrentTutorial && tutorialSelected) {
      const refTopOffset = getBoundingClientRectTopOffset(this.childRef)
      const refTopPosition = getBoundingClientRectTopPosition(this.childRef)
      /**
       * Scrolling order:
       *  1.- If the `ref` is on the top part of the document (1/3 of it) then scroll top
       *  2.- If the `ref` is on the top part of the screen or above it (1/3 of it or above) or
       *      if is on the bottom part or bellow (2/3 of it and under) then scroll to the actual `ref`
       *  3.- In any other case avoid scrolling because the element is currently on the usable part of
       *      the screen (on the 2/3 part of the screen)
       *
       * NOTE:
       * Screen refers to the actual window the user can see on a given time on the browser, meanwhile, document
       * refers to the actual HTML document being rendered that might have part of it's content outside of the
       * current screen.
       */
      const refOnTopSection = refTopPosition < window.innerHeight / 3
      const refAboveWindowTopThird = refTopOffset < window.innerHeight / 3
      const refBelowWindowBottomThird =
        refTopOffset > (window.innerHeight * 2) / 3
      if (refOnTopSection) {
        return scrollToRef(0)
      } else if (refBelowWindowBottomThird || refAboveWindowTopThird) {
        return scrollToRef(refTopPosition, this.offset)
      }
    }
    /* Scroll back to the top of the page after exiting the tutorial */
    if (newCurrentTutorial && tutorialWasSelected && rootCurrentTutorial) {
      return scrollToRef(0)
    }
  }

  handleNext = () => {
    const { incrementTutorial, setTutorial, next, handleNext } = this.props
    if (handleNext) handleNext()
    if (next > 0) {
      setTutorial(next)
    } else {
      incrementTutorial()
    }
  }

  handleClose = () => {
    this.props.setTutorial(0)
  }

  get smallScreen() {
    if (!this._isMounted) return false
    return window.innerWidth < MEDIUM_WIDTH
  }

  get offset() {
    return this.smallScreen ? this.props.mobileOffset : this.props.offset
  }

  get position() {
    return this.smallScreen ? this.props.mobilePosition : this.props.position
  }

  render() {
    const {
      steps,
      tutorial,
      currentTutorial,
      header,
      content,
      children,
      group,
      elmId,
      skip,
      closeOnDimmerClick,
      fade,
      nextButtonContent,
    } = this.props
    const context = {
      handleNext: this.handleNext,
      handleClose: this.handleClose,
      showTooltip: tutorial === currentTutorial,
      tutorialStatus: {
        current: steps.sort((a, b) => a - b).indexOf(tutorial) + 1,
        total: steps.length,
      },
    }

    return (
      <TutorialTooltipGroupContext.Provider value={context}>
        {group ? (
          <TutorialTooltipGroup
            fade={fade}
            elmId={elmId}
            children={children}
            childRef={this.childRef}
          />
        ) : (
          <TutorialTooltip
            fade={fade}
            elmId={elmId}
            header={header}
            content={content}
            skip={skip}
            position={this.position}
            children={children}
            closeOnDimmerClick={closeOnDimmerClick}
            nextButtonContent={nextButtonContent}
            childRef={this.childRef}
          />
        )}
      </TutorialTooltipGroupContext.Provider>
    )
  }
}

const mapProps = ({ tutorial }) => ({
  currentTutorial: tutorial.tutorial,
  steps: tutorial.steps,
})

const mapActions = {
  incrementTutorial: TutorialTooltipActions.incrementTutorial,
  setTutorial: TutorialTooltipActions.setTutorial,
  registerStep: TutorialTooltipActions.registerStep,
  unregisterStep: TutorialTooltipActions.unregisterStep,
}

export default connect(mapProps, mapActions)(TutorialTooltipContainer)

export {
  TutorialTooltipGroupItem,
  TutorialTooltipActions,
  TutorialTooltipReducer,
}
